/**
 * /vs/:vendor — head-to-head comparison SEO landing pages.
 *
 * Goal: capture organic search traffic for "[competitor] vs [us]" /
 * "[competitor] alternative" / "[competitor] pricing" queries — the highest
 * commercial-intent keywords in B2B SaaS.
 *
 * Each vendor renders with:
 *   - Unique <title>, <meta description>, og:image
 *   - JSON-LD: Organization + SoftwareApplication + BreadcrumbList + FAQPage
 *   - Honest "when to pick them vs us" decision matrix
 *   - Side-by-side capability table (reuses the same shape as the
 *     /token-intelligence-suite competitive landscape)
 *   - Pricing honesty: public-data only, never invented numbers
 *
 * Brand: slate-950 + emerald/cyan accents. NO purple/violet/fuchsia.
 */
import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import {
  ArrowLeft, ArrowRight, Check, X, Sparkles, AlertCircle, ShieldCheck,
} from 'lucide-react';
import { COMPETITORS } from '../data/competitorData';

const BASE = 'https://cartieai.com';

const CapabilityCell = ({ value }) => {
  if (value === 'yes' || value === 'world-class' || value === 'full') {
    const label = value === 'world-class' ? 'world-class' : (value === 'full' ? 'full' : 'yes');
    return (
      <div className="flex items-center gap-1.5">
        <Check className="w-4 h-4 text-emerald-400 shrink-0" />
        <span className="text-emerald-300 text-sm">{label}</span>
      </div>
    );
  }
  if (value === 'partial') {
    return (
      <div className="flex items-center gap-1.5">
        <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
        <span className="text-amber-300 text-sm">partial</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <X className="w-4 h-4 text-rose-400 shrink-0" />
      <span className="text-rose-300/70 text-sm">no</span>
    </div>
  );
};

const buildSchema = (comp) => {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: `${BASE}/vs` },
        { '@type': 'ListItem', position: 3, name: `vs ${comp.name}`, item: `${BASE}/vs/${comp.slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'CARTIEAI',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'AI-native Financial Operating System for cloud + AI token costs. Per-feature P&L, PR-time cost prediction, multi-model routing.',
      offers: [
        { '@type': 'Offer', price: '0', priceCurrency: 'USD', name: 'Free tools' },
        { '@type': 'Offer', price: '199', priceCurrency: 'USD', name: 'Pro · monthly', priceSpecification: { '@type': 'UnitPriceSpecification', price: '199', priceCurrency: 'USD', unitText: 'MONTH' } },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: `Is CARTIEAI a replacement for ${comp.name}?`, acceptedAnswer: { '@type': 'Answer', text: comp.summary } },
        { '@type': 'Question', name: `When should I pick ${comp.name} over CARTIEAI?`, acceptedAnswer: { '@type': 'Answer', text: comp.when_to_pick_them.join(' ') } },
        { '@type': 'Question', name: `When should I pick CARTIEAI over ${comp.name}?`, acceptedAnswer: { '@type': 'Answer', text: comp.when_to_pick_us.join(' ') } },
        { '@type': 'Question', name: `How does CARTIEAI pricing compare to ${comp.name}?`, acceptedAnswer: { '@type': 'Answer', text: `${comp.name}: ${comp.pricing.them} CARTIEAI: ${comp.pricing.us}` } },
      ],
    },
  ];
};

const VsVendor = () => {
  const { vendor } = useParams();
  const comp = COMPETITORS[vendor];

  if (!comp) {
    return <Navigate to="/vs" replace />;
  }

  const title = `${comp.h1} | CARTIEAI`;
  const description = comp.subhead;
  const ogImage = `${BASE}/og/vs-${comp.slug}.png`;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`vs-${comp.slug}-page`}>
      <SEOHead
        title={comp.h1}
        description={description}
        canonical={`${BASE}/vs/${comp.slug}`}
        keywords={comp.keywords}
        ogImage={ogImage}
        schema={buildSchema(comp)}
      />

      {/* Sticky nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-950 text-slate-100 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="vs-nav-back">
            <ArrowLeft className="w-4 h-4" /> CARTIEAI
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/vs" className="hidden sm:block text-sm text-slate-400 hover:text-white" data-testid="vs-nav-index">All comparisons</Link>
            <Link to="/honest-scorecard" className="hidden sm:block text-sm text-emerald-300/90 hover:text-emerald-200" data-testid="vs-nav-scorecard">Honest Scorecard</Link>
            <Link to="/login" className="text-sm text-slate-400 hover:text-white" data-testid="vs-nav-signin">Sign in</Link>
            <Link to="/pricing" data-testid="vs-nav-cta">
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
                Start 7-day free <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="space-y-5">
          <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold" data-testid="vs-category-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2 inline-block" />
            Comparison · {comp.category}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-tight" data-testid="vs-h1">
            {comp.h1.split(':')[0]}:
            <span className="block text-emerald-400 mt-1">{comp.h1.split(':')[1]?.trim()}</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-3xl leading-relaxed" data-testid="vs-subhead">
            {comp.subhead}
          </p>
        </div>

        {/* Honest summary callout */}
        <Card className="mt-10 bg-slate-900 text-slate-100 border-slate-800" data-testid="vs-summary-card">
          <CardContent className="p-6 sm:p-7">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-emerald-400 shrink-0 mt-1" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-1.5">TL;DR — Honest take</div>
                <p className="text-slate-300 leading-relaxed">{comp.summary}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* When to pick which */}
        <div className="grid sm:grid-cols-2 gap-5 mt-10">
          <Card className="bg-slate-900 text-slate-100 border-slate-800">
            <CardContent className="p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-3">Pick {comp.name} if</div>
              <ul className="space-y-2.5">
                {comp.when_to_pick_them.map((r, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-400 leading-relaxed">
                    <span className="text-slate-400 shrink-0">›</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/5 border-emerald-500/30" data-testid="vs-pick-us-card">
            <CardContent className="p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-3">Pick CARTIEAI if</div>
              <ul className="space-y-2.5">
                {comp.when_to_pick_us.map((r, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-700 leading-relaxed">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Capability table */}
        <div className="mt-14 space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-1">/ Capability comparison</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="vs-table-h2">CARTIEAI vs {comp.name} — feature by feature</h2>
            <p className="text-sm text-slate-400 mt-1">As of {new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}. Based on publicly documented features. Spotted an inaccuracy? <Link to="/contact" className="text-emerald-400 hover:underline">tell us</Link>.</p>
          </div>
          <Card className="overflow-hidden border-slate-800 bg-slate-900 text-slate-100">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="vs-capability-table">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-950">
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-slate-400 font-bold">Capability</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-emerald-300 font-bold">CARTIEAI</th>
                    <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.15em] text-slate-400 font-bold">{comp.name}</th>
                  </tr>
                </thead>
                <tbody>
                  {comp.comparison.map((row, i) => (
                    <tr key={i} className="border-b border-slate-800/60 last:border-0 hover:bg-slate-900">
                      <td className="px-5 py-3.5 text-slate-300">{row.capability}</td>
                      <td className="px-5 py-3.5"><CapabilityCell value={row.us} /></td>
                      <td className="px-5 py-3.5"><CapabilityCell value={row.them} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        {/* Strategic scope clarification — explains WHY any "no" rows exist
            (deliberate scope choice, not missing feature). World-class
            positioning beats faking capabilities. */}
        {comp.scope_clarification && (
          <Card className="mt-10 bg-slate-900 text-slate-100 border-emerald-500/30" data-testid="vs-scope-clarification">
            <CardContent className="p-6 sm:p-7">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-1" />
                <div className="space-y-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-1.5">Honest scope statement</div>
                    <h3 className="text-lg font-bold text-white">{comp.scope_clarification.title}</h3>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{comp.scope_clarification.body}</p>
                  <div className="border-l-2 border-emerald-500/40 pl-3 text-sm text-slate-100 italic">{comp.scope_clarification.truth}</div>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    <span className="text-emerald-300 font-bold">Recommendation: </span>
                    {comp.scope_clarification.recommendation}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing honesty */}
        <div className="mt-14 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-1">/ Pricing — honestly</div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="vs-pricing-h2">What it actually costs</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <Card className="bg-slate-900 text-slate-100 border-slate-800">
              <CardContent className="p-6">
                <div className="text-sm font-bold text-slate-400 mb-2">{comp.name}</div>
                <p className="text-sm text-slate-400 leading-relaxed">{comp.pricing.them}</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-500/5 border-emerald-500/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <div className="text-sm font-bold text-emerald-300">CARTIEAI</div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{comp.pricing.us}</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom CTA */}
        <Card className="mt-16 bg-slate-900 text-slate-100 border-emerald-500/30 overflow-hidden" data-testid="vs-bottom-cta">
          <CardContent className="p-8 sm:p-12 text-center">
            <h3 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Still evaluating? <span className="text-emerald-400">Try us free.</span>
            </h3>
            <p className="text-slate-400 text-base mt-3 max-w-xl mx-auto">
              No credit card. Real features, not a sandbox. Connect a cloud account or use our seed data. See savings in day 1.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
              <Link to="/pricing">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-7" data-testid="vs-cta-pricing">
                  Start 7-day free <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/savings-calculator">
                <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 px-7" data-testid="vs-cta-calc">
                  Or try the savings calculator
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Other comparisons */}
        <div className="mt-14">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-3">/ Other comparisons</div>
          <div className="flex flex-wrap gap-2">
            <Link to="/honest-scorecard" data-testid="vs-link-scorecard">
              <Badge className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 px-3 py-1.5">
                The full 32-feature Honest Scorecard <ArrowRight className="w-3 h-3 ml-1.5" />
              </Badge>
            </Link>
            {Object.values(COMPETITORS)
              .filter((c) => c.slug !== comp.slug)
              .map((c) => (
                <Link key={c.slug} to={`/vs/${c.slug}`} data-testid={`vs-link-${c.slug}`}>
                  <Badge variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-emerald-500/50 px-3 py-1.5">
                    CARTIEAI vs {c.name} <ArrowRight className="w-3 h-3 ml-1.5" />
                  </Badge>
                </Link>
              ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default VsVendor;
